import React, { useState, useEffect } from 'react';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import { 
  Button, 
  TextField, 
  Container, 
  Typography, 
  Box, 
  Alert, 
  IconButton, 
  InputAdornment,
  FormControlLabel,
  Checkbox,
  Paper,
  Divider,
  Link,
  CircularProgress,
  Fade,
  Tooltip
} from '@mui/material';
import { 
  Visibility, 
  VisibilityOff, 
  School as SchoolIcon,
  Lock as LockIcon,
  Person as PersonIcon,
  Info as InfoIcon,
  CheckCircle as CheckCircleIcon
} from '@mui/icons-material';
import api, { requestPasswordReset } from '../api/api';
import { useAuth } from '../context/AuthContext';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';

const Login: React.FC = () => {
  const navigate = useNavigate();
  const { login, isAuthenticated, user } = useAuth();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [attempts, setAttempts] = useState(0);
  const [resetDialogOpen, setResetDialogOpen] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [resetStatus, setResetStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [resetMessage, setResetMessage] = useState('');

  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  const isEmailValid = emailRegex.test(resetEmail);

  // Redirect if already authenticated
  useEffect(() => {
    console.log('Login component - auth state:', { isAuthenticated, user });
    if (isAuthenticated && user) {
      console.log('User already authenticated, redirecting to dashboard for role:', user.role);
      redirectToDashboard(user.role);
    }
  }, [isAuthenticated, user]);

  const redirectToDashboard = (role: string) => {
    console.log('Redirecting to dashboard for role:', role);
    switch (role) {
      case 'superuser':
        navigate('/superadmin-dashboard');
        break;
      case 'admin':
        navigate('/admin-dashboard');
        break;
      default:
        navigate('/dashboard');
        break;
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const form = new URLSearchParams();
      form.append('username', username);
      form.append('password', password);
      
      const res = await api.post('/login', form, {
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      });
      
      localStorage.setItem('token', (res.data as any).access_token);
      
      // Store refresh token if provided
      if ((res.data as any).refresh_token) {
        localStorage.setItem('refresh_token', (res.data as any).refresh_token);
      }
      
      if (rememberMe) {
        localStorage.setItem('remembered_username', username);
      } else {
        localStorage.removeItem('remembered_username');
      }
      
      login((res.data as any).user);
      
      // Redirect to appropriate dashboard
      redirectToDashboard((res.data as any).user.role);
    } catch (err: any) {
      const errorMessage = err?.response?.data?.detail || 
                         err?.response?.data?.message || 
                         'Invalid username or password';
      setError(errorMessage);
      setAttempts(prev => prev + 1);
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordVisibilityToggle = () => {
    setShowPassword(!showPassword);
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && username && password && !loading) {
      handleSubmit(e as any);
    }
  };

  // Load remembered username on component mount
  useEffect(() => {
    const rememberedUsername = localStorage.getItem('remembered_username');
    if (rememberedUsername) {
      setUsername(rememberedUsername);
      setRememberMe(true);
    }
  }, []);

  // Auto-focus password field when username is entered
  useEffect(() => {
    if (username && !password) {
      const passwordField = document.querySelector('input[type="password"]') as HTMLInputElement;
      if (passwordField) {
        passwordField.focus();
      }
    }
  }, [username, password]);

  // Password reset handler
  const handlePasswordReset = async () => {
    setResetStatus('loading');
    setResetMessage('');
    try {
      const frontendUrl = window.location.origin;
      const res = await requestPasswordReset(resetEmail, frontendUrl);
      setResetStatus('success');
      setResetMessage((res.data as any).message || 'If the email exists, a reset link has been sent.');
      setTimeout(() => {
        setResetEmail('');
        setResetStatus('idle');
        setResetMessage('');
        setResetDialogOpen(false);
      }, 2000); // Optionally close dialog after 2s
    } catch (err: any) {
      setResetStatus('error');
      setResetMessage(err?.response?.data?.message || 'Failed to send reset link.');
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        p: 2,
        position: 'relative',
        overflow: 'hidden',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'url("data:image/svg+xml,%3Csvg width="60" height="60" viewBox="0 0 60 60" xmlns="http://www.w3.org/2000/svg"%3E%3Cg fill="none" fill-rule="evenodd"%3E%3Cg fill="%23ffffff" fill-opacity="0.05"%3E%3Ccircle cx="30" cy="30" r="2"/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")',
          pointerEvents: 'none',
        }
      }}
    >
      <Container maxWidth="sm">
        <Fade in timeout={800}>
          <Paper
            elevation={24}
            sx={{
              p: 4,
              borderRadius: 3,
              background: 'rgba(255, 255, 255, 0.95)',
              backdropFilter: 'blur(10px)',
              border: '1px solid rgba(255, 255, 255, 0.2)',
              position: 'relative'
            }}
          >
            {/* Header */}
            <Box sx={{ textAlign: 'center', mb: 4 }}>
              <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
                <Box
                  sx={{
                    p: 2,
                    borderRadius: '50%',
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    boxShadow: '0 8px 32px rgba(102, 126, 234, 0.3)',
                  }}
                >
                  <img 
                    src="/static/logos/cvision_logo.svg" 
                    alt="CVision Logo" 
                    style={{ 
                      width: '32px', 
                      height: '32px',
                      filter: 'brightness(0) invert(1)' // Make SVG white
                    }} 
                  />
                </Box>
              </Box>
              <Typography 
                component="h1" 
                variant="h4" 
                sx={{ 
                  fontWeight: 700, 
                  color: 'text.primary',
                  mb: 1,
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                }}
              >
                Welcome Back
              </Typography>
              <Typography 
                variant="body1" 
                sx={{ 
                  color: 'text.secondary',
                  mb: 3
                }}
              >
                Sign in to your A3S Systems account
              </Typography>
            </Box>

            {/* Login Form */}
            <Box component="form" onSubmit={handleSubmit}>
              {/* Username Field */}
              <TextField
                margin="normal"
                required
                fullWidth
                label="Username"
                value={username}
                onChange={e => setUsername(e.target.value)}
                onKeyPress={handleKeyPress}
                autoFocus
                disabled={loading}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <PersonIcon color="action" />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  mb: 2,
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    transition: 'all 0.2s ease-in-out',
                    '&:hover fieldset': {
                      borderColor: 'primary.main',
                      borderWidth: '2px',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: 'primary.main',
                      borderWidth: '2px',
                    },
                  },
                }}
              />

              {/* Password Field */}
              <TextField
                margin="normal"
                required
                fullWidth
                label="Password"
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={e => setPassword(e.target.value)}
                onKeyPress={handleKeyPress}
                disabled={loading}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <LockIcon color="action" />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <Tooltip title={showPassword ? "Hide password" : "Show password"}>
                        <IconButton
                          onClick={handlePasswordVisibilityToggle}
                          edge="end"
                          disabled={loading}
                          size="small"
                        >
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </Tooltip>
                    </InputAdornment>
                  ),
                }}
                sx={{
                  mb: 2,
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    transition: 'all 0.2s ease-in-out',
                    '&:hover fieldset': {
                      borderColor: 'primary.main',
                      borderWidth: '2px',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: 'primary.main',
                      borderWidth: '2px',
                    },
                  },
                }}
              />

              {/* Remember Me & Forgot Password */}
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={rememberMe}
                      onChange={(e) => setRememberMe(e.target.checked)}
                      disabled={loading}
                      color="primary"
                    />
                  }
                  label="Remember me"
                  sx={{ color: 'text.secondary' }}
                />
                <Tooltip title="Reset your password">
                  <Button
                    variant="text"
                    size="small"
                    sx={{ color: 'primary.main', textTransform: 'none', fontWeight: 500 }}
                    onClick={() => setResetDialogOpen(true)}
                  >
                    Forgot password?
                  </Button>
                </Tooltip>
              </Box>

              {/* Error Alert */}
              {error && (
                <Alert 
                  severity="error" 
                  sx={{ 
                    mb: 3,
                    borderRadius: 2,
                    '& .MuiAlert-icon': {
                      alignItems: 'center'
                    }
                  }}
                >
                  {error}
                  {attempts > 2 && (
                    <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                      Multiple failed attempts detected. Please contact your administrator if you continue to have issues.
                    </Typography>
                  )}
                </Alert>
              )}

              {/* Submit Button */}
              <Button
                type="submit"
                fullWidth
                variant="contained"
                disabled={loading || !username || !password}
                sx={{
                  py: 1.5,
                  borderRadius: 2,
                  fontSize: '1.1rem',
                  fontWeight: 600,
                  textTransform: 'none',
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  boxShadow: '0 4px 12px rgba(102, 126, 234, 0.3)',
                  '&:hover': {
                    boxShadow: '0 6px 20px rgba(102, 126, 234, 0.4)',
                    transform: 'translateY(-1px)',
                  },
                  '&:disabled': {
                    background: 'rgba(0, 0, 0, 0.12)',
                    color: 'rgba(0, 0, 0, 0.38)',
                    boxShadow: 'none',
                    transform: 'none',
                  },
                  transition: 'all 0.2s ease-in-out',
                }}
              >
                {loading ? (
                  <CircularProgress size={24} color="inherit" />
                ) : (
                  'Sign In'
                )}
              </Button>
            </Box>

            {/* After the login form, add the application link */}
            <Box sx={{ mt: 3, textAlign: 'center' }}>
              <Typography variant="body2" color="text.secondary">
                New school?{' '}
                <Link component={RouterLink} to="/apply" color="primary" fontWeight={600} underline="hover">
                  Apply to join the platform
                </Link>
              </Typography>
            </Box>

            {/* Divider */}
            <Divider sx={{ my: 3 }}>
              <Typography variant="body2" color="text.secondary">
                or
              </Typography>
            </Divider>

            {/* Additional Links */}
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Don't have an account?
              </Typography>
              <Link
                href="#"
                variant="body2"
                sx={{
                  color: 'primary.main',
                  textDecoration: 'none',
                  fontWeight: 600,
                  '&:hover': {
                    textDecoration: 'underline',
                  },
                }}
              >
                Contact your administrator
              </Link>
            </Box>

            {/* Footer */}
            <Box sx={{ textAlign: 'center', mt: 4, pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
              <Typography variant="caption" color="text.secondary">
                © {new Date().getFullYear()} A3S Systems. All rights reserved.
              </Typography>
            </Box>
          </Paper>
        </Fade>
      </Container>

      {/* Password Reset Dialog */}
      <Dialog open={resetDialogOpen} onClose={() => { setResetDialogOpen(false); setResetStatus('idle'); setResetMessage(''); }}>
        <DialogTitle sx={{ textAlign: 'center', fontWeight: 700 }}>Reset Password</DialogTitle>
        <DialogContent sx={{ minWidth: 320, py: 2 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2, textAlign: 'center' }}>
            Enter your email address and we'll send you a link to reset your password.
          </Typography>
          <TextField
            autoFocus
            margin="dense"
            label="Email Address"
            type="email"
            fullWidth
            value={resetEmail}
            onChange={e => setResetEmail(e.target.value)}
            disabled={resetStatus === 'loading' || resetStatus === 'success'}
            error={!!resetEmail && !isEmailValid}
            helperText={!!resetEmail && !isEmailValid ? 'Enter a valid email address' : ' '}
            aria-label="Email address for password reset"
          />
          {resetMessage && (
            <Alert icon={resetStatus === 'success' ? <CheckCircleIcon fontSize="inherit" color="success" /> : undefined} severity={resetStatus === 'success' ? 'success' : 'error'} sx={{ mt: 2, textAlign: 'center' }}>{resetMessage}</Alert>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => { setResetDialogOpen(false); setResetStatus('idle'); setResetMessage(''); }}>Cancel</Button>
          <Button
            onClick={handlePasswordReset}
            disabled={!isEmailValid || resetStatus === 'loading' || resetStatus === 'success'}
            variant="contained"
            aria-label="Send password reset link"
          >
            {resetStatus === 'loading' ? <CircularProgress size={20} /> : 'Send Reset Link'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Login;
